'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import {
  Star,
  Clock,
  MapPin,
  Heart,
  ArrowRight,
  Utensils,
  Hotel,
  Car,
  Users,
  Phone,
  CheckCircle,
} from 'lucide-react';
import type { TourPackage, PickupCity } from '../data/packagesData';
import { toast } from 'sonner';

interface PackageCardProps {
  pkg: TourPackage;
  pickupCity?: PickupCity;
}

const INCLUDE_ICONS: Record<string, React.ReactNode> = {
  Meals: <Utensils size={11} />,
  Breakfast: <Utensils size={11} />,
  Hotel: <Hotel size={11} />,
  Houseboat: <Hotel size={11} />,
  Camping: <Hotel size={11} />,
  Dharamshala: <Hotel size={11} />,
  'Beach Resort': <Hotel size={11} />,
  Vehicle: <Car size={11} />,
  'Vehicle (Hatchback/SUV)': <Car size={11} />,
  'Vehicle (Innova)': <Car size={11} />,
  'Vehicle (SUV/Innova)': <Car size={11} />,
  'Vehicle (Tempo Traveller/Innova)': <Car size={11} />,
  SUV: <Car size={11} />,
  Innova: <Car size={11} />,
  Tempo: <Car size={11} />,
  Taxi: <Car size={11} />,
  Driver: <Car size={11} />,
  Fuel: <Car size={11} />,
};

export default function PackageCard({ pkg, pickupCity = 'pathankot' }: PackageCardProps) {
  const [wishlisted, setWishlisted] = useState(false);

  const displayPrice = pkg.pickupPricing[pickupCity];
  const displayOriginal = Math.round(displayPrice * (100 / (100 - pkg.discount)));

  const whatsappMsg = encodeURIComponent(
    `Hi, I'm interested in the "${pkg.name}" package. Please share more details.\n\nPickup: ${pickupCity.charAt(0).toUpperCase() + pickupCity.slice(1)}\nDuration: ${pkg.duration}\nBudget: ₹${displayPrice.toLocaleString('en-IN')}`
  );

  return (
    <div className="package-card group flex flex-col h-full">
      {/* Image */}
      <div className="relative overflow-hidden h-52 flex-shrink-0">
        <AppImage
          src={pkg.image}
          alt={pkg.alt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />

        {/* Wishlist */}
        <button
          onClick={() => {
            setWishlisted(!wishlisted);
            toast(wishlisted ? 'Removed from wishlist' : 'Added to wishlist ❤️');
          }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-150 ${
            wishlisted
              ? 'bg-red-500 text-white shadow-md'
              : 'bg-white/20 text-white hover:bg-white/40'
          }`}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart size={15} className={wishlisted ? 'fill-white' : ''} />
        </button>

        <span className={`absolute top-3 left-3 ${pkg.tagColor}`}>{pkg.tag}</span>
        <span className="absolute bottom-3 right-3 bg-secondary text-white text-xs font-bold px-2 py-1 rounded-lg">
          {pkg.discount}% OFF
        </span>

        {/* Duration + Category */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <span className="flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
            <Clock size={11} />
            {pkg.duration}
          </span>
          <span className="flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
            <MapPin size={11} />
            {pkg.category}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-foreground text-base leading-snug mb-1">{pkg.name}</h3>
        <p className="text-muted-foreground text-xs mb-2 flex items-center gap-1">
          <MapPin size={11} />
          {pkg.destination}
        </p>

        <p className="text-muted-foreground text-xs leading-relaxed mb-3 line-clamp-2">
          {pkg.description}
        </p>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={`${pkg.id}-star-${i}`}
                size={12}
                className={
                  i < Math.floor(pkg.rating) ? 'text-accent fill-current' : 'text-muted-foreground'
                }
              />
            ))}
          </div>
          <span className="text-xs font-bold text-foreground">{pkg.rating}</span>
          <span className="text-xs text-muted-foreground">({pkg.reviews} reviews)</span>
        </div>

        {/* Travel Type Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {pkg.travelType.slice(0, 3).map((t) => (
            <span
              key={`${pkg.id}-type-${t}`}
              className="flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full"
            >
              <Users size={10} />
              {t}
            </span>
          ))}
        </div>

        {/* Highlights */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {pkg.highlights.slice(0, 3).map((h) => (
            <span
              key={`${pkg.id}-hl-${h}`}
              className="text-xs bg-primary-light text-primary px-2 py-0.5 rounded-full font-medium"
            >
              {h}
            </span>
          ))}
          {pkg.highlights.length > 3 && (
            <span className="text-xs text-muted-foreground px-2 py-0.5">
              +{pkg.highlights.length - 3} more
            </span>
          )}
        </div>

        {/* Key Inclusions */}
        <div className="flex items-center flex-wrap gap-1.5 mb-3">
          {['Vehicle', 'Driver', 'Fuel', 'Toll Tax'].map((inc) => (
            <div
              key={`${pkg.id}-inc-${inc}`}
              className="flex items-center gap-1 text-xs text-secondary font-medium bg-secondary-light px-2 py-1 rounded-full"
            >
              <CheckCircle size={10} />
              {inc}
            </div>
          ))}
        </div>

        {/* Best Time */}
        <p className="text-xs text-muted-foreground mb-3">
          🗓 Best Time: <span className="font-medium text-foreground">{pkg.bestTimeToVisit}</span>
        </p>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Price + CTAs */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-end justify-between mb-3">
            <div>
              <span className="text-xs text-muted-foreground">Starting from</span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-extrabold text-primary font-tabular">
                  ₹{displayPrice.toLocaleString('en-IN')}
                </span>
                <span className="text-xs text-muted-foreground line-through font-tabular">
                  ₹{displayOriginal.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Season</div>
              <div className="text-xs font-semibold text-foreground">
                {pkg.season.join(', ')}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <a
              href={`https://wa.me/918894915859?text=${whatsappMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 border-2 border-[#25D366] text-[#25D366] font-semibold text-sm py-2 rounded-xl hover:bg-[#25D366] hover:text-white transition-all duration-150"
            >
              <Phone size={13} />
              WhatsApp
            </a>
            <Link
              href={`/tour-packages/${pkg.id}`}
              className="flex-1 btn-primary text-sm py-2 justify-center"
            >
              View Details
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}